import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useCategories from "../../Lib/usefetchCategories";
import useSearchVessels from "../lib/useSearchVessels";
import useFetchVessels from "../lib/useFetchVessels";
import { AuthContext } from "../../Context/AuthContext";
import { useToast } from "../../Context/ToastContext";
import Skeleton from "react-loading-skeleton";
import { BsFilter } from "react-icons/bs";
import "react-loading-skeleton/dist/skeleton.css";
import "./our_vessels.css";

const OurVessels = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useContext(AuthContext);
  const { showToast } = useToast();
  const { data: categoriesResponse, isLoading, error } = useCategories();
  const { vessels: allVessels, vesselsError, vesselsLoading } = useFetchVessels();
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  
  // State for filter sections
  // const [expandedSections, setExpandedSections] = useState({
  //   type: true,
  //   paxAccommodation: false,
  //   use: false,
  //   price: false,
  //   paymentPlan: false,
  //   classification: false
  // });

  // State for selected filters
  const [selectedFilters, setSelectedFilters] = useState({
    type: [],
    paxAccommodation: [],
    use: [],
    price: [],
    paymentPlan: [],
    classification: []
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Use search hook
  const { searchResults, searchError, searchLoading } = useSearchVessels(
    debouncedSearchTerm,
    selectedFilters
  );

  // Show toast errors
  useEffect(() => {
    if (searchError) {
      showToast(searchError.message, 'error');
    }
  }, [searchError, showToast]);

  useEffect(() => {
    if (vesselsError) {
      showToast(vesselsError.message, 'error');
    }
  }, [vesselsError, showToast]);

  // Toggle section expansion
  // const toggleSection = (section) => {
  //   setExpandedSections(prev => ({
  //     ...prev,
  //     [section]: !prev[section]
  //   }));
  // };

  // Handle filter selection
  // const handleFilterSelect = (category, value) => {
  //   setSelectedFilters(prev => {
  //     const currentFilters = prev[category];
  //     const newFilters = currentFilters.includes(value)
  //       ? currentFilters.filter(filter => filter !== value)
  //       : [...currentFilters, value];
      
  //     return {
  //       ...prev,
  //       [category]: newFilters
  //     };
  //   });
  // };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedFilters({
      type: [],
      paxAccommodation: [],
      use: [],
      price: [],
      paymentPlan: [],
      classification: []
    });
    setSelectedCategory("All");
  };

  // Navigation function
  const viewVesselDetails = (vesselId) => {
    navigate(`/charterer-dashboard/vessels/${vesselId}`);
  };

  const categories = categoriesResponse?.data || [];
  
  // Use search results if available, otherwise use API data
  // Filter out busy vessels from search results as well
  const vessels = searchResults.length > 0 
    ? searchResults.filter(vessel => vessel.active === "available" || vessel.available === true)
    : allVessels;

  // Filter options - updated to match API expected values
  // const filterOptions = {
  //   type: ["AHTS 001", "PSV 001", "SEV 001", "FSIV 001"],
  //   paxAccommodation: ["10-20", "21-30", "31-40", "41+"],
  //   use: [
  //     "cars", "trucks", "security", "chemical", "crude oil", 
  //     "cargo transport", "passenger transport", "specialized maritime services",
  //     "military and defense", "offshore industry support", "fishing and aquaculture",
  //     "leisure and recreation", "environmental protection", "transportation of livestock",
  //     "mining and resource extraction", "construction and infrastructure",
  //     "tourism and expedition", "medical and humanitarian aid"
  //   ],
  //   price: ["$1000-$2000", "$2001-$3000", "$3001+"],
  //   paymentPlan: ["Daily", "Weekly", "Monthly"],
  //   classification: [
  //     "American Bureau of Shipping (ABS)",
  //     "Bureau Veritas (BV)",
  //     "China Classification Society (CCS)",
  //     "Croatian Register of Shipping(CRS)",
  //     "Det Norske Veritas (DNV)",
  //     "Germanischer Lloyd (GL)",
  //     "Indian Register of Shipping(IR)",
  //     "Korean Register (KR)",
  //     "Lloyd's Register (LR)",
  //     "Nippon Kaiji Kyokai (Class NK)",
  //     "Polish Register of Shipping(PRS)",
  //     "Registro Italiano Navale (RINA)",
  //     "Russian Maritime Register of Shipping (RS)"
  //   ]
  // };

  // Filter vessels based on selected filters and category (only for local filtering when not using search)
  const filterVessels = (vessels) => {
    // If we have search results, the API handles filtering
    if (searchResults.length > 0) {
      return vessels;
    }
    
    return vessels.filter(vessel => {
      // Only show available vessels (exclude busy ones)
      const isAvailable = vessel.active === "available" || vessel.available === true;
      if (!isAvailable) {
        return false;
      }

      // Handle different vessel data structures
      const primary = vessel.primaryInformation || {};
      const vesselType = primary.vesselType || vessel.type || vessel.vesselType;
      const vesselUse = primary.vesselUse || vessel.use || vessel.vesselUse;
      const vesselPax = vessel.vesselInformation?.numberOfPassengers || vessel.paxAccommodation || vessel.numberOfPassengers;
      const vesselPrice = primary.pricing || vessel.price || vessel.pricing;
      const vesselPaymentPlan = primary.paymentPlan || vessel.paymentPlan;
      const vesselClassification = vessel.vesselInformation?.classification || vessel.classification;

      // Category filter
      const categoryMatch = selectedCategory === "All" || vesselType === selectedCategory;

      // If no sidebar filters are selected, only apply category filter
      const hasActiveFilters = Object.values(selectedFilters).some(filters => filters.length > 0);
      if (!hasActiveFilters) {
        return categoryMatch;
      }

      // Check each sidebar filter category
      const typeMatch = selectedFilters.type.length === 0 || selectedFilters.type.includes(vesselType);
      
      const paxMatch = selectedFilters.paxAccommodation.length === 0 || 
        selectedFilters.paxAccommodation.some(range => {
          const [min, max] = range.split("-").map(num => parseInt(num));
          const pax = parseInt(vesselPax);
          if (max === undefined) {
            // Handle "41+" case
            return pax >= min;
          }
          return pax >= min && pax <= max;
        });
      
      const useMatch = selectedFilters.use.length === 0 || 
        (Array.isArray(vesselUse) 
          ? vesselUse.some(use => selectedFilters.use.includes(use))
          : selectedFilters.use.includes(vesselUse));
      
      const priceMatch = selectedFilters.price.length === 0 || 
        selectedFilters.price.some(range => {
          const price = parseInt(vesselPrice?.toString().replace(/[^0-9]/g, "") || "0");
          const [min, max] = range.replace(/[$,]/g, "").split("-").map(num => parseInt(num));
          if (max === undefined) {
            // Handle "$3001+" case
            return price >= min;
          }
          return price >= min && price <= max;
        });
      
      const paymentPlanMatch = selectedFilters.paymentPlan.length === 0 || 
        selectedFilters.paymentPlan.includes(vesselPaymentPlan);
      
      const classificationMatch = selectedFilters.classification.length === 0 || 
        selectedFilters.classification.includes(vesselClassification);

      return categoryMatch && typeMatch && paxMatch && useMatch && priceMatch && paymentPlanMatch && classificationMatch;
    });
  };

  // Get filtered vessels
  const filteredVessels = filterVessels(vessels);

  return (
    <div className="our-vessels-container">
      <div className="wrap">
        {/* Content */}
        {!isAuthenticated && !token ? (
          <div className="categories-auth-error">
            <h3>Please log in to view categories.</h3>
          </div>
        ) : (
          <div className="content">
            <div className="vessel">
              <div className="links">
                <button 
                  onClick={() => setSelectedCategory("All")}
                  className={`all-button ${selectedCategory === "All" ? "active" : ""}`}
                >
                  All
                </button>

                {isLoading &&
                  Array.from({ length: 4 }).map((_, idx) => (
                    <Skeleton key={idx} height={40} width={100} className="category-skeleton" />
                  ))}
                {error && <p>Error fetching categories</p>}

                {categories.length > 0
                  ? categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`category-button ${selectedCategory === category ? "active" : ""}`}
                      >
                        {category}
                      </button>
                    ))
                  : !isLoading && <p>No categories available</p>}
              </div>
              
              {/* Search Bar with Disabled Filter */}
              <div className="search-only-container">
                <div className="search-section">
                  <button className="filter-button-disabled" disabled>
                    <BsFilter />
                    <span>Filter</span>
                  </button>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search vessels by type (AHTS, PSV, SEV, FSIV)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    {searchLoading && (
                      <div className="search-loading">
                        <Skeleton height={20} width={20} circle />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="vessel-list">
              {(isLoading || searchLoading || vesselsLoading) ? (
                <Skeleton count={4} height={200} />
              ) : filteredVessels.length > 0 ? (
                filteredVessels.map((vessel) => {
                  const primary = vessel.primaryInformation || {};
                  const info = vessel.vesselInformation || {};
                  const image = (vessel.images && vessel.images[0]) || 'https://res.cloudinary.com/dquhjbqzf/image/upload/v1725646641/Rectangle_206004_pyb09n.png';
                  
                  return (
                    <div key={vessel._id || vessel.id} className="vessel-card">
                      <div className="vessel-image-container">
                        <img
                          src={image}
                          alt={primary.vesselType || 'Vessel'}
                          className="vessel-image"
                          onError={e => { e.target.src = 'https://res.cloudinary.com/dquhjbqzf/image/upload/v1725646641/Rectangle_206004_pyb09n.png'; }}
                        />
                      </div>
                      <div className="vessel-details">
                        <div className="vessel-info">
                          <h3 className="vessel-name">{primary.vesselType || 'Vessel'}</h3>
                          <div className="spec-item">
                            <span className="spec-value">{info.bollardPull || '48'}T Bollard Pull</span>
                          </div>
                          <div className="spec-item">
                            <span className="spec-value">{info.numberOfPassengers || '18'} Pax Accommodation</span>
                          </div>
                          <div className="spec-item">
                            <span className="spec-value">{info.deckSpace || '125'} m² Deck Space</span>
                          </div>
                          <div className="spec-item">
                            <span className="spec-value">{info.deckStrength || '5'}T/m² Deck Strength</span>
                          </div>
                          <div className="spec-item">
                            <span className="spec-value">{info.bhp || '4,200'} BHP</span>
                          </div>
                          <div className="spec-item">
                            <span className="spec-value">Classification: {info.classification?.split(' ')[0] || 'ABS'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="vessel-pricing">
                        <div className="price">${primary.pricing?.toLocaleString() || '2000'}/Day</div>
                        <button 
                          onClick={() => viewVesselDetails(vessel._id)}
                          className="spec-link"
                        >
                          View specification sheet
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-results">
                  <p>
                    {vessels.length === 0 
                      ? "No vessels have been uploaded yet. Check back later for available vessels."
                      : searchTerm 
                        ? `No vessels found matching "${searchTerm}"` 
                        : "No vessels match the selected filters"
                    }
                  </p>
                  {vessels.length > 0 && (searchTerm || Object.values(selectedFilters).some(filters => filters.length > 0)) && (
                    <button 
                      onClick={() => {
                        clearAllFilters();
                        setSearchTerm("");
                      }}
                      className="clear-search-btn"
                    >
                      Clear search and filters
                    </button>
                  )}
                </div>
              )}
              
              {/* Pagination */}
              {filteredVessels.length > 0 && (
                <div className="pagination">
                  <button className="pagination-btn">
                    <span>&lt;</span>
                  </button>
                  <button className="pagination-btn active">1</button>
                  <button className="pagination-btn">2</button>
                  <button className="pagination-btn">3</button>
                  <button className="pagination-btn">4</button>
                  <span className="pagination-ellipsis">...</span>
                  <button className="pagination-btn">10</button>
                  <button className="pagination-btn">
                    <span>&gt;</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      

    </div>
  );
};

export default OurVessels;

